import React, { createContext, useContext, useCallback } from 'react';
import type { ToastType } from '../components/ui/Toast';
import { showBrandedToast } from '../utils/notifications';

/**
 * Toast context — kept for API compatibility with components that
 * already use `useToast()` (e.g., SessionExpirationHandler,
 * NewCompRequestPage). Internally every show* method now routes to
 * the same react-hot-toast renderer as notify.*, so the app has a
 * single toast pipeline rather than two parallel ones.
 *
 * The previous implementation maintained its own toast queue + a
 * standalone <ToastContainer> mount; that's gone — react-hot-toast's
 * <Toaster /> in main.tsx is the only renderer.
 */

interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string, duration?: number, persistent?: boolean) => void;
  showSuccess: (title: string, message?: string, persistent?: boolean) => void;
  showError: (title: string, message?: string, persistent?: boolean) => void;
  showWarning: (title: string, message?: string, persistent?: boolean) => void;
  showInfo: (title: string, message?: string, persistent?: boolean) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
  /** Retained for API compatibility — the underlying renderer always
   *  uses /colliers-logo.svg now. */
  logoSrc?: string;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const showToast = useCallback(
    (
      type: ToastType,
      title: string,
      message?: string,
      duration?: number,
      persistent: boolean = false
    ) => {
      showBrandedToast(type, title, message, { duration, persistent });
    },
    []
  );

  const showSuccess = useCallback(
    (title: string, message?: string, persistent: boolean = false) => {
      showBrandedToast('success', title, message, { duration: 4000, persistent });
    },
    []
  );

  const showError = useCallback(
    (title: string, message?: string, persistent: boolean = false) => {
      showBrandedToast('error', title, message, { duration: 6000, persistent });
    },
    []
  );

  const showWarning = useCallback(
    (title: string, message?: string, persistent: boolean = false) => {
      showBrandedToast('warning', title, message, { duration: 5000, persistent });
    },
    []
  );

  const showInfo = useCallback(
    (title: string, message?: string, persistent: boolean = false) => {
      showBrandedToast('info', title, message, { duration: 4000, persistent });
    },
    []
  );

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return <ToastContext.Provider value={contextValue}>{children}</ToastContext.Provider>;
};

export default ToastContext;
